// src/components/CyranoPage.jsx
// Cyrano Communications Studio — outreach drafting, cadence building,
// message library, and style coaching. All grounded in live account signals.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useProfile } from '../context/UserProfileContext';
import './CyranoPage.css';
import { apiFetch } from '../lib/api';
// The starter suggestions moved out of this file on 16 Oct 2026. They named one
// industry's market throughout — "a cold CIO pursuit at a mid-market carrier",
// "how to reference a Guidewire implementation signal" — which for an MGA writing
// management liability points at the wrong SIDE of the market, not merely the
// wrong seniority: a carrier is who an MGA places business WITH. See the header
// of cyranoStarters.js for the rule the replacements follow.
import { STARTER_PROMPTS_BY_TYPE, STARTER_PROMPTS } from '../lib/cyranoStarters';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'compose',  icon: '✍️',  label: 'Compose' },
  { id: 'staging',  icon: '📋',  label: 'Staging' },
  { id: 'cadence',  icon: '📬',  label: 'Cadence Builder' },
  { id: 'library',  icon: '📚',  label: 'Message Library' },
  { id: 'coach',    icon: '🎓',  label: 'Style Coach' },
  { id: 'content',  icon: '🚀',  label: 'Content Studio' },
];

const OUTREACH_TYPES = [
  { id: 'first_touch',    label: 'First Touch',         icon: '🚀', desc: 'Cold or warm intro' },
  { id: 'follow_up',      label: 'Follow-Up',           icon: '🔁', desc: 'No response yet' },
  { id: 'sequence',       label: '5-Touch Sequence',    icon: '📬', desc: 'Full signal-led cadence' },
  { id: 'meeting_request',label: 'Meeting Request',     icon: '📅', desc: 'Book a specific call' },
  { id: 'post_meeting',   label: 'Post-Meeting',        icon: '📝', desc: 'Recap & next steps' },
  // "After submitting SOW" was consulting-specific — an MGA sends a quote or a
  // binder, not a statement of work.
  { id: 'proposal_follow',label: 'Proposal Follow-Up',  icon: '📄', desc: 'After submitting a proposal' },
  { id: 'exec_intro',     label: 'Executive Intro',     icon: '🤝', desc: 'Referral or warm intro' },
  { id: 'custom',         label: 'Custom / Urgent',     icon: '⚡', desc: 'Describe your own need' },
];

const LIBRARY_CONTENT = [
  {
    id: 1,
    title: 'First Touch — New Leader (Change Signal)',
    type: 'First Touch',
    persona: 'Decision-maker',
    signal: 'Leadership Change',
    subject: '[Initiative] at [Account]',
    preview: 'A new leader usually inherits a plan they did not build. This template opens with that reality and positions us as the firm that helps new leaders get ahead of it.',
    tags: ['First Touch', 'Leadership Change', 'New Leader'],
  },
  {
    id: 2,
    title: 'Follow-Up — No Response (Value Add)',
    type: 'Follow-Up',
    persona: 'Decision-maker',
    signal: 'Any',
    subject: 'One thought on [specific challenge]',
    preview: 'Adds a fresh angle rather than just checking in. References a specific development in their market to justify the follow-up and give the recipient a reason to respond.',
    tags: ['Follow-Up', 'Value Add'],
  },
  {
    id: 3,
    title: 'Meeting Request — Budget Season',
    type: 'Meeting Request',
    persona: 'Budget holder',
    signal: 'Financial / Budget',
    subject: '20 minutes before Q1 planning closes',
    preview: 'Budget season creates urgency without manufacturing false pressure. This template ties a specific meeting ask to the account\'s planning cycle.',
    tags: ['Budget Holder', 'Meeting Request', 'Budget'],
  },
  {
    id: 4,
    title: 'Post-Meeting — Discovery Call Recap',
    type: 'Post-Meeting',
    persona: 'Any',
    signal: 'Any',
    subject: 'Notes from our conversation + next step',
    preview: 'Sent within 2 hours. Three sections: what you heard, what you\'ll do next, one forward question. Short. No lengthy recap.',
    tags: ['Post-Meeting', 'Discovery', 'Any Persona'],
  },
  {
    id: 5,
    title: 'Executive Intro — Referral',
    type: 'Executive Intro',
    persona: 'C-Suite',
    signal: 'Referral',
    subject: '[Referrer name] suggested I reach out',
    // "the most relevant DigitalMe credential" named the product itself, which
    // is a brand this base must not carry — the exavalu_relevance rule.
    preview: 'Name-drops the referrer immediately. Leads with our most relevant credential for this specific buyer. Single clear ask.',
    tags: ['Referral', 'C-Suite', 'Executive Intro'],
  },
  {
    id: 6,
    title: 'Re-Engagement — Gone Dark (60+ Days)',
    type: 'Follow-Up',
    persona: 'Any',
    signal: 'Any',
    subject: 'Quick question about [their priority]',
    preview: 'After 60 days of silence, don\'t apologize — re-engage with a specific question about something that matters to them. Shows you\'ve been paying attention.',
    tags: ['Re-Engagement', 'Follow-Up', 'Any Persona'],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// CYRANO AVATAR
// ─────────────────────────────────────────────────────────────────────────────

function CyranoAvatar({ size = 40, thinking = false }) {
  return (
    <div
      className={`cyrano-avatar${thinking ? ' cyrano-avatar--thinking' : ''}`}
      style={{ width: size, height: size, borderRadius: size * 0.28 }}
    >
      <span style={{ fontSize: size * 0.38, fontWeight: 900, color: 'var(--dm-white)', letterSpacing: '-1px' }}>C</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACCOUNT SELECTOR
// ─────────────────────────────────────────────────────────────────────────────

function AccountSelector({ value, onChange }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');

  useEffect(() => {
    setLoading(true);
    apiFetch('/api/accounts/list?page_size=100')
      .then(r => r.json())
      .then(d => { if (d.accounts) setAccounts(d.accounts); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = accounts.filter(a =>
    a.accountName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="cyrano-selector">
      <label className="cyrano-selector-label">Account</label>
      <input
        className="cyrano-selector-search"
        placeholder="Search accounts..."
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <div className="cyrano-selector-list">
        {loading && <div className="cyrano-selector-empty">Loading...</div>}
        {!loading && filtered.length === 0 && <div className="cyrano-selector-empty">No accounts found</div>}
        {filtered.map(a => (
          <button
            key={a.id}
            className={`cyrano-selector-item ${value?.id === a.id ? 'cyrano-selector-item--active' : ''}`}
            onClick={() => onChange({ ...a, name: a.accountName })}
            type="button"
          >
            <span className="cyrano-selector-item-name">{a.accountName}</span>
            {a.tier && <span className="cyrano-selector-item-meta">{a.tier}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTACT SELECTOR
// ─────────────────────────────────────────────────────────────────────────────

function ContactSelector({ accountId, value, onChange }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    if (!accountId) { setContacts([]); return; }
    setLoading(true);
    apiFetch(`/api/contacts/by-account?accountId=${accountId}`)
      .then(r => r.json())
      .then(d => { if (d.contacts) setContacts(d.contacts); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [accountId]);

  if (!accountId) return null;

  return (
    <div className="cyrano-selector cyrano-selector--contact">
      <label className="cyrano-selector-label">Contact (optional)</label>
      <div className="cyrano-selector-list cyrano-selector-list--short">
        <button
          className={`cyrano-selector-item ${!value ? 'cyrano-selector-item--active' : ''}`}
          onClick={() => onChange(null)}
          type="button"
        >
          <span className="cyrano-selector-item-name">— No specific contact</span>
        </button>
        {loading && <div className="cyrano-selector-empty">Loading...</div>}
        {contacts.map(c => (
          <button
            key={c.id}
            className={`cyrano-selector-item ${value?.id === c.id ? 'cyrano-selector-item--active' : ''}`}
            onClick={() => onChange(c)}
            type="button"
          >
            <span className="cyrano-selector-item-name">{c.name}</span>
            <span className="cyrano-selector-item-meta">{c.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// CUSTOM BRIEF SETUP — conversational guided setup for Custom / Urgent type
// ─────────────────────────────────────────────────────────────────────────────

const BRIEF_QUESTIONS = [
  // The placeholders named one market's buyers and vendors — "this carrier is
  // going out to RFP", "their former CIO now at Majesco". A placeholder is read
  // as an example of what belongs in the box, so a wrong one teaches the wrong
  // thing to every tenant outside that market.
  { id: 'situation', label: "What's the situation?",   placeholder: "e.g. We just heard this account is going out to bid next month. I need to reach the decision-maker before they finalize the shortlist." },
  { id: 'contact',   label: "Who are you writing to?", placeholder: "e.g. The buyer for this line, never spoken with them. We have a mutual contact — a former colleague of theirs." },
  { id: 'goal',      label: "What needs to happen?",   placeholder: "e.g. A 20-minute intro call in the next two weeks. Not pitching yet — just getting on their radar before the RFP." },
  { id: 'tone',      label: "Any tone constraints?",   placeholder: "e.g. Urgent but not desperate. Peer-to-peer, no vendor-speak." },
];

function CustomBriefSetup({ brief, onChange }) {
  const parseBrief = (text) => {
    if (!text) return {};
    const result = {};
    BRIEF_QUESTIONS.forEach(q => {
      const marker = `[${q.id.toUpperCase()}]`;
      const idx = text.indexOf(marker);
      if (idx !== -1) {
        const start = idx + marker.length;
        const nextMarker = BRIEF_QUESTIONS.map(q2 => `[${q2.id.toUpperCase()}]`).find(m => text.indexOf(m) > idx);
        const end = nextMarker ? text.indexOf(nextMarker) : text.length;
        result[q.id] = text.slice(start, end).replace(/^\n+|\n+$/g, '').trim();
      }
    });
    return result;
  };

  const [fields, setFields]   = React.useState(() => parseBrief(brief));
  const [activeQ, setActiveQ] = React.useState('situation');
  const textareaRef           = React.useRef(null);

  const updateField = (id, value) => {
    const next = { ...fields, [id]: value };
    setFields(next);
    const assembled = BRIEF_QUESTIONS
      .filter(q => next[q.id]?.trim())
      .map(q => `[${q.id.toUpperCase()}]\n${next[q.id].trim()}`)
      .join('\n\n');
    onChange(assembled);
  };

  const filledCount = BRIEF_QUESTIONS.filter(q => fields[q.id]?.trim().length > 5).length;
  const isReady     = filledCount >= 2;

  React.useEffect(() => { if (textareaRef.current) textareaRef.current.focus(); }, [activeQ]);

  return (
    <div className="cyrano-custom-setup">
      <div className="cyrano-custom-setup-header">
        <span className="cyrano-custom-setup-icon">⚡</span>
        <div>
          <div className="cyrano-custom-setup-title">Tell Cyrano about your situation</div>
          <div className="cyrano-custom-setup-sub">Answer 2–4 questions. The more detail, the better the draft.</div>
        </div>
        {isReady && <div className="cyrano-custom-setup-ready">✓ Ready</div>}
      </div>

      <div className="cyrano-custom-progress">
        {BRIEF_QUESTIONS.map(q => (
          <div key={q.id}
            className={`cyrano-custom-progress-dot${fields[q.id]?.trim().length > 5 ? ' filled' : ''}${activeQ === q.id ? ' active' : ''}`}
            onClick={() => setActiveQ(q.id)} title={q.label} />
        ))}
        <span className="cyrano-custom-progress-label">{filledCount} of {BRIEF_QUESTIONS.length} filled</span>
      </div>

      <div className="cyrano-custom-questions">
        {BRIEF_QUESTIONS.map((q, i) => {
          const isActive = activeQ === q.id;
          const isFilled = fields[q.id]?.trim().length > 5;
          return (
            <div key={q.id}
              className={`cyrano-custom-q${isActive ? ' cyrano-custom-q--active' : ''}${isFilled ? ' cyrano-custom-q--filled' : ''}`}
              onClick={() => !isActive && setActiveQ(q.id)}
            >
              <div className="cyrano-custom-q-header">
                <span className="cyrano-custom-q-num">{i + 1}</span>
                <span className="cyrano-custom-q-label">{q.label}</span>
                {isFilled && !isActive && <span className="cyrano-custom-q-check">✓</span>}
              </div>
              {isActive ? (
                <div className="cyrano-custom-q-body">
                  <textarea
                    ref={textareaRef}
                    className="cyrano-custom-q-input"
                    placeholder={q.placeholder}
                    value={fields[q.id] || ''}
                    onChange={e => updateField(q.id, e.target.value)}
                    rows={4}
                  />
                  <div className="cyrano-custom-q-actions">
                    {i > 0 && <button className="cyrano-custom-q-back" onClick={() => setActiveQ(BRIEF_QUESTIONS[i-1].id)} type="button">← Back</button>}
                    {q.id === 'tone' && <button className="cyrano-custom-q-skip" onClick={() => updateField(q.id, 'Professional and direct. No jargon.')} type="button">Use default</button>}
                    {i < BRIEF_QUESTIONS.length - 1 && <button className="cyrano-custom-q-next" onClick={() => setActiveQ(BRIEF_QUESTIONS[i+1].id)} type="button" disabled={!fields[q.id]?.trim()}>Next →</button>}
                  </div>
                </div>
              ) : (
                fields[q.id]?.trim() && <div className="cyrano-custom-q-preview">{fields[q.id].trim().slice(0, 120)}{fields[q.id].trim().length > 120 ? '…' : ''}</div>
              )}
            </div>
          );
        })}
      </div>

      {isReady && (
        <div className="cyrano-custom-setup-footer">
          ✓ Context loaded — type your request in the chat below or use a starter prompt
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OUTREACH TYPE PICKER — includes Custom / Urgent with brief field
// ─────────────────────────────────────────────────────────────────────────────

function OutreachTypePicker({ value, onChange, customBrief, onCustomBriefChange }) {
  return (
    <div className="cyrano-outreach-picker">
      <label className="cyrano-selector-label">Outreach Type</label>
      <div className="cyrano-outreach-grid">
        {OUTREACH_TYPES.map(t => (
          <button
            key={t.id}
            className={`cyrano-outreach-tile ${value === t.id ? 'cyrano-outreach-tile--active' : ''}${t.id === 'custom' ? ' cyrano-outreach-tile--custom' : ''}`}
            onClick={() => onChange(t.id)}
            type="button"
          >
            <span className="cyrano-outreach-icon">{t.icon}</span>
            <span className="cyrano-outreach-label">{t.label}</span>
            <span className="cyrano-outreach-desc">{t.desc}</span>
          </button>
        ))}
      </div>

      {/* Custom brief — conversational setup panel */}
      {value === 'custom' && (
        <CustomBriefSetup brief={customBrief} onChange={onCustomBriefChange} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SIGNAL PILLS (sidebar)
// ─────────────────────────────────────────────────────────────────────────────

function SignalPill({ signal }) {
  const colors = {
    fresh: 'var(--dm-green)', recent: 'var(--dm-amber)', aging: '#f97316',
    stale: 'var(--dm-red)', 'very-stale': '#991b1b', unknown: 'var(--dm-grey-600)',
  };
  const labels = {
    fresh: '🔥 Fresh', recent: '⚡ Recent', aging: '⚠️ Aging',
    stale: '🕰️ Stale', 'very-stale': '🪦 Very Stale', unknown: '?',
  };
  return (
    <div className="cyrano-signal-pill">
      <div className="cyrano-signal-top">
        <span className="cyrano-signal-name">{signal.name}</span>
        <span className="cyrano-signal-age" style={{ color: colors[signal.freshness] || 'var(--dm-grey-600)' }}>
          {labels[signal.freshness] || signal.freshness}
        </span>
      </div>
      {signal.ageInDays != null && (
        <span className="cyrano-signal-days">{signal.ageInDays}d old</span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHAT MESSAGE
// ─────────────────────────────────────────────────────────────────────────────

function ChatMessage({ message, isThinking }) {
  const isAI = message.role === 'assistant';

  const renderContent = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h3 key={i} className="cyrano-msg-h2">{line.slice(3)}</h3>;
      if (line.startsWith('# '))  return <h2 key={i} className="cyrano-msg-h1">{line.slice(2)}</h2>;
      if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
        return <p key={i} className="cyrano-msg-bold">{line.slice(2, -2)}</p>;
      }
      if (line.startsWith('- ') || line.startsWith('• ')) {
        return (
          <div key={i} className="cyrano-msg-bullet">
            <span className="cyrano-msg-bullet-dot">▸</span>
            <span>{formatInline(line.slice(2))}</span>
          </div>
        );
      }
      if (line.match(/^\d+\. /)) {
        const num  = line.match(/^(\d+)\. /)[1];
        const rest = line.replace(/^\d+\. /, '');
        return (
          <div key={i} className="cyrano-msg-numbered">
            <span className="cyrano-msg-num">{num}.</span>
            <span>{formatInline(rest)}</span>
          </div>
        );
      }
      if (line.trim() === '') return <div key={i} className="cyrano-msg-spacer" />;
      return <p key={i} className="cyrano-msg-p">{formatInline(line)}</p>;
    });
  };

  const formatInline = (text) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : part
    );

  return (
    <div className={`cyrano-msg ${isAI ? 'cyrano-msg--ai' : 'cyrano-msg--user'}`}>
      {isAI && (
        <div className="cyrano-msg-avatar">
          <CyranoAvatar size={32} thinking={isThinking} />
        </div>
      )}
      <div className="cyrano-msg-bubble">
        {isThinking ? (
          <div className="cyrano-thinking"><span /><span /><span /></div>
        ) : (
          <div className="cyrano-msg-content">{renderContent(message.content)}</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OUTCOME LOGGER
// ─────────────────────────────────────────────────────────────────────────────

const OUTCOME_OPTIONS = [
  { id: 'Replied',        icon: '↩️', label: 'They Replied',     color: 'var(--dm-green)' },
  { id: 'Meeting Booked', icon: '📅', label: 'Meeting Booked',   color: 'var(--dm-purple-vivid)' },
  { id: 'Forwarded',      icon: '➡️', label: 'Forwarded On',     color: 'var(--dm-cyan)' },
  { id: 'No Reply',       icon: '🔇', label: 'No Reply',         color: 'var(--dm-grey-500)' },
  { id: 'Unsubscribed',   icon: '🚫', label: 'Unsubscribed',     color: 'var(--dm-red)' },
];

function OutcomeLogger({ account, contact, outreachType, signals, lastDraft, onLogged }) {
  const [open,    setOpen]    = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [notes,   setNotes]   = useState('');
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);

  if (!lastDraft) return null;

  const freshestSignal = signals.find(s => s.freshness === 'fresh') ||
                         signals.find(s => s.freshness === 'recent') ||
                         signals[0] || null;

  const handleSave = async () => {
    if (!outcome) return;
    setSaving(true);
    try {
      await apiFetch('/api/cyrano/log-outcome', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          accountId:       account?.id || '',
          accountName:     account?.name || '',
          contactName:     contact?.name || '',
          contactTitle:    contact?.title || '',
          outreachType,
          signalUsed:      freshestSignal?.name || '',
          signalFreshness: freshestSignal?.freshness || '',
          outcome,
          notes,
          messageSnippet:  lastDraft.slice(0, 300),
        }),
      });
      setSaved(true);
      setTimeout(() => { setOpen(false); setSaved(false); setOutcome(null); setNotes(''); }, 1500);
      if (onLogged) onLogged(outcome);
    } catch (err) {
      console.error('Failed to log outcome:', err);
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return <div className="cyrano-outcome-saved">✓ Logged — Cyrano will learn from this</div>;
  }

  return (
    <div className="cyrano-outcome-logger">
      {!open ? (
        <button className="cyrano-outcome-trigger" onClick={() => setOpen(true)} type="button">
          📤 Did you send this? Log the result so Cyrano can learn
        </button>
      ) : (
        <div className="cyrano-outcome-panel">
          <div className="cyrano-outcome-header">
            <span className="cyrano-outcome-title">What happened when you sent this?</span>
            <button className="cyrano-outcome-close" onClick={() => setOpen(false)} type="button">✕</button>
          </div>
          <div className="cyrano-outcome-options">
            {OUTCOME_OPTIONS.map(o => (
              <button
                key={o.id}
                className={`cyrano-outcome-btn ${outcome === o.id ? 'cyrano-outcome-btn--active' : ''}`}
                style={outcome === o.id ? { borderColor: o.color, color: o.color, background: o.color + '18' } : {}}
                onClick={() => setOutcome(o.id)}
                type="button"
              >
                <span>{o.icon}</span>
                <span>{o.label}</span>
              </button>
            ))}
          </div>
          <textarea
            className="cyrano-outcome-notes"
            placeholder="Optional: what worked, what didn't, any context Cyrano should know..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
          />
          <div className="cyrano-outcome-footer">
            <span className="cyrano-outcome-hint">This trains Cyrano on what actually works for your buyers</span>
            <button
              className="cyrano-outcome-save"
              onClick={handleSave}
              disabled={!outcome || saving}
              type="button"
            >
              {saving ? 'Saving...' : 'Log Result'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CHAT PANEL
// ─────────────────────────────────────────────────────────────────────────────

function ChatPanel({ tab, account, contact, signals, outreachType, customBrief, initialContext }) {
  // P0.30 30.2 — see VictorPage: the name comes from context, not a prop.
  const { commsName } = useProfile();
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState(initialContext || '');
  const [streaming, setStreaming] = useState(false);
  const [error,     setError]     = useState(null);
  const [lastDraft, setLastDraft] = useState(null);
  const [outcomeLogged, setOutcomeLogged] = useState(false);
  const bottomRef = useRef(null);
  const abortRef  = useRef(null);

  useEffect(() => {
    setMessages([]);
    setInput('');
    setError(null);
    setLastDraft(null);
    setOutcomeLogged(false);
    if (abortRef.current) abortRef.current.abort();
  }, [tab, account?.id, contact?.id, outreachType]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const sendMessage = useCallback(async (text) => {
    const content = (text || input).trim();
    if (!content || streaming) return;

    setInput('');
    setError(null);

    const userMsg     = { role: 'user', content };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setStreaming(true);
    setMessages(prev => [...prev, { role: 'assistant', content: '', _streaming: true }]);

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      let finalContent = content;
      if (tab === 'content') {
        // The brief that used to be prepended here — voice, audience, the
        // Cyrano's Notes section — now lives in api/cyrano/chat.js under
        // `mode === 'content'`. It hardcoded "P&C insurance executives (CIO,
        // CTO, VP IT, CFO)" for every tenant, and could not do better from the
        // browser: the vertical and the tenant's derived buyer_roles are not
        // reachable here. It is a system instruction in any case, so putting it
        // in the user's turn was the wrong place for it twice over.
        //
        // `mode: tab` is already sent below, so the server knows this is a
        // Content Studio request without a flag.
        finalContent = 'CONTENT STUDIO REQUEST\n\nRequest: ' + content;
      } else if ((tab === 'compose' || tab === 'cadence') && outreachType && newMessages.length === 1) {
        const typeLabel    = OUTREACH_TYPES.find(t => t.id === outreachType)?.label || outreachType;
        const isSequence   = outreachType === 'sequence' || tab === 'cadence';
        const contactStr   = contact?.name
          ? contact.name + (contact.title ? ', ' + contact.title : '') + (contact.influenceLevel ? ' [' + contact.influenceLevel + ' influence]' : '')
          : 'contact not specified';
        const accountStr   = account?.name || 'account not specified';
        const customContext = outreachType === 'custom' && customBrief ? '\nSITUATION BRIEF: ' + customBrief : '';

        // Signal weighting context
        const topSignal   = signals.length > 0 ? signals.reduce((a, b) => (b.signal_score || 0) > (a.signal_score || 0) ? b : a, signals[0]) : null;
        const topScore    = topSignal?.signal_score || 0;
        const signalBlock = topSignal
          ? '\nBEST AVAILABLE SIGNAL: "' + topSignal.name + '" [Score: ' + topScore + '/25, Confidence: ' + (topSignal.confidence_score || '?') + '/5]\n' + (topSignal.interpretation || '')
          : '\nNO SCORED SIGNALS AVAILABLE — use industry pattern framing.';

        const weightInstruction = topScore >= 18
          ? 'Signal score is HIGH (18+). Anchor directly on the signal. Be specific. Introduce urgency.'
          : topScore >= 14
          ? 'Signal score is ACTIONABLE (14-17). Reference signal indirectly. Insight-driven. Soft CTA.'
          : topScore >= 10
          ? 'Signal score is STRATEGIC WATCH (10-13). Use pattern recognition framing only. Do NOT make direct claims.'
          : 'Signal score is LOW or absent. Use INDUSTRY PATTERN framing — do not anchor on specific signal.';

        if (isSequence) {
          // Sequence generation prompt
          const seqType = topScore >= 18 ? 'High Intent (Direct, specific, time-sensitive)'
            : topScore >= 14 ? 'Medium Intent (Insight-driven, build curiosity)'
            : 'Low Signal (Curiosity only, no direct claims)';

          finalContent = 'SEQUENCE GENERATION REQUEST\n\n' +
            'Account: ' + accountStr + '\nContact: ' + contactStr + customContext + '\n' +
            signalBlock + '\n\nSignal Weight: ' + weightInstruction + '\nSequence Type: ' + seqType + '\n\nRequest: ' + content + '\n\n' +
            'Generate a 5-touch outreach sequence in Gary\'s voice.\n\n' +
            'Rules:\n' +
            '- Each touch introduces NEW thinking — never repeat\n' +
            '- Escalate slightly in directness across touches\n' +
            '- Touch 5 is always a respectful breakup\n' +
            '- 75-150 words per touch maximum\n' +
            '- Gary voice: short sentences, natural phrasing, no buzzwords\n' +
            '- Every touch: one observation + one implied risk + one open loop\n\n' +
            'Output format:\n\n' +
            'Sequence Type: [type]\nStrategy: [one sentence]\n\n' +
            'Touch 1:\nSubject: [subject]\nBody:\n[message]\n\n' +
            'Touch 2 (3-5 days):\nSubject: [subject]\nBody:\n[message]\n\n' +
            'Touch 3 (5-7 days):\nSubject: [subject]\nBody:\n[message]\n\n' +
            'Touch 4 (7-10 days):\nSubject: [subject]\nBody:\n[message]\n\n' +
            'Touch 5 — Breakup (10-14 days):\nSubject: [subject]\nBody:\n[message]\n\n' +
            'Strategy Notes:\n- Why this sequence works:\n- Risk:\n- Confidence:';
        } else {
          // Single message compose prompt
          finalContent = 'OUTREACH REQUEST\n\n' +
            'Type: ' + typeLabel + '\nAccount: ' + accountStr + '\nContact: ' + contactStr + customContext + '\n' +
            signalBlock + '\n\nSignal Weight: ' + weightInstruction + '\n\nRequest: ' + content + '\n\n' +
            'Step 1 — Evaluate BEFORE writing:\n' +
            '- Is the signal strong enough to anchor outreach?\n' +
            '- What is the real risk or tension this creates for the buyer?\n' +
            '- What would this executive actually care about here?\n\n' +
            'Step 2 — Draft in Gary\'s voice. Structure: Observation → Insight → Implication → Offer → Close.\n' +
            '75-150 words maximum. One message, one signal.\n\n' +
            'Step 3 — Output format (mandatory):\n\n' +
            'Subject: [subject line]\n\nBody:\n[clean message only]\n\n' +
            'Strategy Note:\n- Signal used:\n- Objective:\n- Why it works:\n- Risk in this approach:\n- Confidence:';
        }
      }

      const messagesWithContext = newMessages.map((m, idx) =>
        idx === newMessages.length - 1 ? { ...m, content: finalContent } : m
      );

      const res = await apiFetch('/api/cyrano/chat', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        signal:  controller.signal,
        body:    JSON.stringify({
          messages:     messagesWithContext,
          accountId:    account?.id || null,
          accountName:  account?.name || '',
          contact:      contact ? { name: contact.name, title: contact.title, email: contact.email, influenceLevel: contact.influenceLevel } : {},
          mode:         tab,
          customBrief:  outreachType === 'custom' ? customBrief : '',
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader   = res.body.getReader();
      const decoder  = new TextDecoder();
      let   buffer   = '';
      let   fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              fullText += parsed.text;
              const snapshot = fullText;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: snapshot, _streaming: true };
                return updated;
              });
            }
          } catch {}
        }
      }

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', content: fullText };
        return updated;
      });
      if (tab === 'compose') {
        setLastDraft(fullText);
        setOutcomeLogged(false);
      }

      // Jarvis observation — fire and forget, highest priority room for integrity
      apiFetch('/api/jarvis/observe', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room: 'cyrano_studio', ai: 'cyrano', sessionType: 'cyrano',
          accountId: account?.id || null,
          response: fullText,
          context: {
            accountName:  account?.name || null,
            contactNames: contact?.name ? [contact.name] : [],
            signals:      [],
          },
          thread: newMessages.slice(-6),
        }),
      }).catch(() => {});

    } catch (err) {
      if (err.name === 'AbortError') return;
      setError('Cyrano encountered an error. Try again.');
      setMessages(prev => prev.filter(m => !m._streaming));
    } finally {
      setStreaming(false);
    }
  }, [input, messages, streaming, tab, account, contact, outreachType, customBrief, signals]);

  const starters = tab === 'compose'
    ? (STARTER_PROMPTS_BY_TYPE[outreachType] || STARTER_PROMPTS_BY_TYPE['first_touch'])
    : (STARTER_PROMPTS[tab] || []);

  const outreachTypeLabel = OUTREACH_TYPES.find(t => t.id === outreachType)?.label || 'Message';
  const needsAccount = tab === 'compose' && !account;
  const needsBrief = tab === 'compose' && outreachType === 'custom' && (!customBrief || customBrief.trim().length < 20);

  return (
    <div className="cyrano-chat">
      {messages.length === 0 && (
        <div className="cyrano-empty">
          {needsAccount ? (
            <div className="cyrano-empty-blocked">
              <CyranoAvatar size={48} />
              <p className="cyrano-empty-blocked-text">Select an account on the left to load signal context.</p>
            </div>
          ) : needsBrief ? (
            <div className="cyrano-empty-blocked">
              <CyranoAvatar size={48} />
              <p className="cyrano-empty-blocked-text">
                ⚡ Describe your situation in the brief field on the left — the more context you give, the better Cyrano can respond to your specific need.
              </p>
            </div>
          ) : (
            <>
              <CyranoAvatar size={48} />
              <h3 className="cyrano-empty-title">
                {tab === 'compose' && `${outreachTypeLabel}${account ? ` — ${account.name}` : ''}`}
                {tab === 'cadence' && 'Cadence Builder'}
                {tab === 'coach'   && 'Style Coach'}
              </h3>
              <p className="cyrano-empty-sub">
                {tab === 'compose' && outreachType === 'custom'
                  ? `Brief loaded. Tell ${commsName} what to draft — ${commsName} will use your situation context to write messaging that fits your exact need.`
                  : tab === 'compose'
                  ? `Tell ${commsName} what to draft. Select your outreach type, account, and contact for a Signal-Led ${outreachTypeLabel.toLowerCase()} grounded in what is actually happening at this account.`
                  : tab === 'cadence'
                  ? 'Describe the pursuit and Cyrano will map out the full Signal-Led Selling sequence — touch by touch, with question types, angles, and timing.'
                  : 'Paste a draft you have written and Cyrano will diagnose whether it is pushing or questioning — then rewrite the weak parts specifically.'}
              </p>
              <div className="cyrano-starters">
                {starters.map((s, i) => (
                  <button key={i} className="cyrano-starter" onClick={() => sendMessage(s)} type="button">
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {messages.length > 0 && (
        <div className="cyrano-messages">
          {messages.map((msg, i) => (
            <ChatMessage
              key={i}
              message={msg}
              isThinking={msg._streaming && msg.content === ''}
            />
          ))}
          <div ref={bottomRef} />
        </div>
      )}

      {tab === 'compose' && lastDraft && !streaming && !outcomeLogged && (
        <OutcomeLogger
          account={account}
          contact={contact}
          outreachType={outreachType}
          signals={signals}
          lastDraft={lastDraft}
          onLogged={() => setOutcomeLogged(true)}
        />
      )}

      {error && <div className="cyrano-chat-error">⚠️ {error}</div>}

      <div className="cyrano-input-area">
        <div className="cyrano-input-wrap">
          <textarea
            className="cyrano-input"
            placeholder={
              needsAccount
                ? 'Select an account first...'
                : needsBrief
                ? 'Describe your situation in the brief field first...'
                : tab === 'compose'  ? 'Tell Cyrano what to draft...'
                : tab === 'cadence' ? 'Describe the pursuit for your cadence...'
                : 'Paste a draft or ask Cyrano for coaching...'
            }
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            disabled={streaming || needsAccount || needsBrief}
            rows={1}
          />
          <button
            className={`cyrano-send ${streaming ? 'cyrano-send--streaming' : ''}`}
            onClick={() => sendMessage()}
            disabled={!input.trim() || streaming || needsAccount || needsBrief}
            type="button"
          >
            {streaming
              ? <span className="cyrano-send-stop">■</span>
              : <span className="cyrano-send-arrow">↑</span>
            }
          </button>
        </div>
        {messages.length > 0 && (
          <button className="cyrano-clear" onClick={() => { setMessages([]); setError(null); }} type="button">
            Clear conversation
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MESSAGE LIBRARY TAB
// ─────────────────────────────────────────────────────────────────────────────


// ── Content Studio Tab ────────────────────────────────────────────────────────
// Selma-powered content suggestions grounded in live signals + news
// Clicking a suggestion pre-loads Cyrano Compose with full context


// ── Victor × Cyrano Content Studio Panel ──────────────────────────────────────
// Phase 1 (strategy): Victor asks 4 questions one at a time, reacting to answers
// Phase 2 (brief):    Victor synthesizes and hands off to Cyrano
// Phase 3 (writing):  Cyrano writes the full piece with Cyrano's Notes
// Cross-session memory: saves to Learned Patterns DB on completion


function ContentAgentAvatar({ agent, size = 36 }) {
  const isVictor = agent === 'victor';
  return (
    <div style={{
      width: size, height: size, borderRadius: 10, flexShrink: 0,
      background: isVictor
        ? 'linear-gradient(135deg, #7C3AED, #A855F7)'
        : 'linear-gradient(135deg, #1E2A5A, #2F5BFF)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.45, color: 'white', fontWeight: 700,
      boxShadow: `0 2px 8px ${isVictor ? 'rgba(124,58,237,0.4)' : 'rgba(47,91,255,0.4)'}`,
    }}>
      {isVictor ? 'V' : 'C'}
    </div>
  );
}

function ContentMessage({ msg }) {
  const isUser   = msg.role === 'user';
  const isVictor = msg.agent === 'victor';
  const isBrief  = msg.isBrief;

  if (isUser) {
    return (
      <div className="cs-chat-msg cs-chat-msg-user">
        <div className="cs-chat-bubble cs-chat-bubble-user">{msg.content}</div>
      </div>
    );
  }

  return (
    <div className="cs-chat-msg cs-chat-msg-agent">
      <ContentAgentAvatar agent={isVictor ? 'victor' : 'cyrano'} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="cs-chat-agent-label" style={{ color: isVictor ? '#A78BFA' : '#93C5FD' }}>
          {isVictor ? 'Victor · Strategy' : 'Cyrano · Writing'}
          {isBrief && <span className="cs-brief-badge">Content Brief</span>}
        </div>
        <div className={`cs-chat-bubble cs-chat-bubble-agent ${isBrief ? 'cs-chat-bubble-brief' : ''}`}>
          {msg._streaming && msg.content === ''
            ? <span className="cs-thinking-dots"><span /><span /><span /></span>
            : <span style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</span>
          }
        </div>
      </div>
    </div>
  );
}

function ContentChatPanel({ signals }) {
  const [messages,    setMessages]    = useState([]);
  const [input,       setInput]       = useState('');
  const [streaming,   setStreaming]   = useState(false);
  const [phase,       setPhase]       = useState('strategy'); // strategy | brief | writing | done
  const [questionNum, setQuestionNum] = useState(1);
  const [brief,       setBrief]       = useState('');
  const [error,       setError]       = useState(null);
  const [saved,       setSaved]       = useState(false);
  const bottomRef  = useRef(null);
  const abortRef   = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Victor opens the session automatically
  useEffect(() => {
    startSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function startSession() {
    setStreaming(true);
    const openingMsg = { role: 'assistant', agent: 'victor', content: '', _streaming: true };
    setMessages([openingMsg]);

    try {
      const res = await apiFetch('/api/cyrano/content-strategy', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages:    [{ role: 'user', content: 'Start the content strategy session. Ask your first question.' }],
          phase:       'strategy',
          questionNum: 1,
          signals,
        }),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => res.status);
        throw new Error(`API error ${res.status}: ${errText}`);
      }
      await streamResponse(res, 0, 'victor');
    } catch (e) {
      console.error('[ContentChatPanel] startSession failed:', e.message);
      setError(`Victor could not start the session: ${e.message}`);
      setStreaming(false);
    }
  }

  async function streamResponse(res, msgIndex, agent) {
    const reader  = res.body.getReader();
    const decoder = new TextDecoder();
    let   buffer  = '';
    let   full    = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6);
        if (data === '[DONE]') break;
        try {
          const parsed = JSON.parse(data);
          if (parsed.text) {
            full += parsed.text;
            const snap = full;
            setMessages(prev => {
              const updated = [...prev];
              updated[msgIndex] = { ...updated[msgIndex], content: snap, _streaming: true };
              return updated;
            });
          }
        } catch {}
      }
    }

    setMessages(prev => {
      const updated = [...prev];
      updated[msgIndex] = { ...updated[msgIndex], content: full, _streaming: false };
      return updated;
    });

    setStreaming(false);
    return full;
  }

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setError(null);

    const userMsg    = { role: 'user', content: text };
    const agentPlaceholder = {
      role: 'assistant',
      agent: phase === 'writing' ? 'cyrano' : 'victor',
      content: '',
      _streaming: true,
    };

    const newMessages = [...messages, userMsg, agentPlaceholder];
    setMessages(newMessages);
    setStreaming(true);

    const msgIndex = newMessages.length - 1;

    try {
      // Determine next phase
      const nextQ = questionNum + 1;
      const isLastAnswer = phase === 'strategy' && questionNum >= 4;
      const nextPhase = isLastAnswer ? 'brief' : phase;

      const res = await apiFetch('/api/cyrano/content-strategy', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages:    [...messages, userMsg].map(m => ({ role: m.role, content: m.content })),
          phase:       nextPhase,
          questionNum: nextQ,
          brief,
          signals,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const fullResponse = await streamResponse(res, msgIndex, nextPhase === 'brief' ? 'victor' : phase === 'writing' ? 'cyrano' : 'victor');

      // Detect brief completion and auto-trigger Cyrano
      if (nextPhase === 'brief' || phase === 'brief') {
        const briefMatch = fullResponse.match(/---CONTENT BRIEF---([\s\S]*?)---END BRIEF---/);
        if (briefMatch) {
          const extractedBrief = briefMatch[1].trim();
          setBrief(extractedBrief);

          // Mark as brief message
          setMessages(prev => {
            const updated = [...prev];
            updated[msgIndex] = { ...updated[msgIndex], isBrief: true };
            return updated;
          });

          // Small delay then auto-kick Cyrano
          setTimeout(() => triggerCyrano(extractedBrief, [...messages, userMsg, { role: 'assistant', content: fullResponse }]), 800);
          setPhase('writing');
          return;
        }
      }

      if (isLastAnswer) {
        setPhase('brief');
      } else if (phase === 'strategy') {
        setQuestionNum(nextQ);
      }

    } catch (e) {
      if (e.name === 'AbortError') return;
      setError('Something went wrong. Try again.');
      setMessages(prev => prev.filter(m => !m._streaming));
      setStreaming(false);
    }
  }, [input, messages, streaming, phase, questionNum, brief, signals]); // eslint-disable-line react-hooks/exhaustive-deps

  async function triggerCyrano(extractedBrief, priorMessages) {
    setStreaming(true);
    const cyranoPlaceholder = {
      role: 'assistant', agent: 'cyrano', content: '', _streaming: true,
    };
    setMessages(prev => [...prev, cyranoPlaceholder]);

    try {
      const res = await apiFetch('/api/cyrano/content-strategy', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages:    [{ role: 'user', content: 'Write the piece now.' }],
          phase:       'writing',
          brief:       extractedBrief,
          signals,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Stream directly into the last message
      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let   buffer  = '';
      let   full    = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              full += parsed.text;
              const snap = full;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', agent: 'cyrano', content: snap, _streaming: true };
                return updated;
              });
            }
          } catch {}
        }
      }

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: 'assistant', agent: 'cyrano', content: full, _streaming: false };
        return updated;
      });
      setPhase('done');
      setStreaming(false);

      // Save to Learned Patterns
      if (full && extractedBrief) {
        apiFetch('/api/cyrano/content-strategy', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ savePiece: true, brief: extractedBrief, finalPiece: full, contentType: 'content' }),
        }).catch(() => {});
        setSaved(true);
      }

    } catch (e) {
      setError('Cyrano encountered an error writing the piece. Try again.');
      setStreaming(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleReset() {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setInput('');
    setPhase('strategy');
    setQuestionNum(1);
    setBrief('');
    setError(null);
    setSaved(false);
    setStreaming(false);
    setTimeout(() => startSession(), 100);
  }

  const phaseLabel = {
    strategy: `Victor · Question ${questionNum} of 4`,
    brief:    'Victor · Building Brief',
    writing:  'Cyrano · Writing',
    done:     'Complete',
  }[phase];

  const phaseColor = (phase === 'writing' || phase === 'done') ? '#93C5FD' : '#A78BFA';

  return (
    <div className="cs-chat-root">

      {/* Phase indicator bar */}
      <div className="cs-phase-bar">
        <div className="cs-phase-steps">
          {['strategy','brief','writing','done'].map((p, i) => (
            <React.Fragment key={p}>
              <div className={`cs-phase-step${phase === p ? ' cs-phase-step-active' : ''}${(phase !== p && ['brief','writing','done'].includes(phase) && ['strategy','brief','writing'].includes(p) && ['strategy','brief','writing','done'].indexOf(p) < ['strategy','brief','writing','done'].indexOf(phase)) ? ' cs-phase-step-done' : ''}`}>
                <div className="cs-phase-dot" />
                <span>{p === 'strategy' ? 'Strategy' : p === 'brief' ? 'Brief' : p === 'writing' ? 'Writing' : 'Done'}</span>
              </div>
              {i < 3 && <div className="cs-phase-line" />}
            </React.Fragment>
          ))}
        </div>
        <div className="cs-phase-label" style={{ color: phaseColor }}>{phaseLabel}</div>
        <button className="cs-new-btn" onClick={handleReset} disabled={streaming}>
          + New Piece
        </button>
      </div>

      {/* Messages */}
      <div className="cs-chat-messages">
        {messages.map((msg, i) => (
          <ContentMessage key={i} msg={msg} />
        ))}
        {error && <div className="cs-chat-error">⚠️ {error}</div>}
        {saved && phase === 'done' && (
          <div className="cs-saved-notice">✦ Session saved to Learned Patterns — Victor and Cyrano will remember this.</div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input — hidden during writing/done phases */}
      {(phase === 'strategy' || phase === 'brief') && (
        <div className="cs-chat-input-area">
          <div className="cs-chat-input-wrap">
            <textarea
              ref={textareaRef}
              className="cs-chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={streaming ? 'Victor is thinking...' : 'Your answer...'}
              disabled={streaming}
              rows={2}
            />
            <button
              className="cs-chat-send"
              onClick={handleSend}
              disabled={streaming || !input.trim()}
            >
              {streaming ? '...' : '→'}
            </button>
          </div>
          <div className="cs-chat-hint">Enter to send · Shift+Enter for new line</div>
        </div>
      )}

      {phase === 'done' && (
        <div className="cs-done-bar">
          <span>Piece complete.</span>
          <button className="cs-new-btn cs-new-btn-prominent" onClick={handleReset}>
            Write another piece →
          </button>
        </div>
      )}
    </div>
  );
}

function LibraryTab() {
  const [filter, setFilter] = useState('All');
  const types = ['All', 'First Touch', 'Follow-Up', 'Meeting Request', 'Post-Meeting', 'Executive Intro'];

  const filtered = filter === 'All'
    ? LIBRARY_CONTENT
    : LIBRARY_CONTENT.filter(m => m.type === filter);

  return (
    <div className="cyrano-library">
      <div className="cyrano-library-header">
        <div className="cyrano-library-intro">
          <h3 className="cyrano-library-title">Message Library</h3>
          <p className="cyrano-library-sub">
            Proven outreach frameworks. Each template is a starting point — use Compose to personalize with live account signals.
          </p>
        </div>
        <div className="cyrano-library-filters">
          {types.map(t => (
            <button
              key={t}
              className={`cyrano-filter-pill ${filter === t ? 'cyrano-filter-pill--active' : ''}`}
              onClick={() => setFilter(t)}
              type="button"
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="cyrano-library-grid">
        {filtered.map(msg => (
          <div key={msg.id} className="cyrano-library-card">
            <div className="cyrano-library-card-top">
              <span className="cyrano-library-type">{msg.type}</span>
              <span className="cyrano-library-persona">{msg.persona}</span>
            </div>
            <h4 className="cyrano-library-card-title">{msg.title}</h4>
            <div className="cyrano-library-subject">
              <span className="cyrano-library-subject-label">Subject:</span>
              <span className="cyrano-library-subject-text">{msg.subject}</span>
            </div>
            <p className="cyrano-library-preview">{msg.preview}</p>
            <div className="cyrano-library-tags">
              {msg.tags.map(tag => (
                <span key={tag} className="cyrano-library-tag">{tag}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SIDEBAR
// ─────────────────────────────────────────────────────────────────────────────

function ContextSidebar({ tab, account, contact, signals, outreachType, customBrief, onChangeAccount, onChangeContact, onChangeOutreachType, onChangeCustomBrief }) {
  const showAccount  = tab === 'compose' || tab === 'cadence';
  const showContact  = tab === 'compose' && account;
  const showOutreach = tab === 'compose';

  return (
    <aside className="cyrano-sidebar">
      <div className="cyrano-sidebar-section">
        <div className="cyrano-sidebar-heading">Context</div>
        <p className="cyrano-sidebar-hint">
          {tab === 'compose'  && 'Select account and contact. Cyrano will ground the draft in live signals.'}
          {tab === 'cadence'  && 'Select an account to load signals for your cadence sequence.'}
          {tab === 'library'  && 'Browse proven outreach frameworks. Personalize with Compose.'}
          {tab === 'coach'    && 'Paste your draft in the chat. No account context needed.'}
        </p>
      </div>

      {showAccount && (
        <div className="cyrano-sidebar-section">
          <AccountSelector value={account} onChange={onChangeAccount} />
        </div>
      )}

      {showContact && (
        <div className="cyrano-sidebar-section">
          <ContactSelector accountId={account?.id} value={contact} onChange={onChangeContact} />
        </div>
      )}

      {showOutreach && (
        <div className="cyrano-sidebar-section">
          <OutreachTypePicker
            value={outreachType}
            onChange={onChangeOutreachType}
            customBrief={customBrief}
            onCustomBriefChange={onChangeCustomBrief}
          />
        </div>
      )}

      {account && signals.length > 0 && (
        <div className="cyrano-sidebar-section">
          <div className="cyrano-sidebar-heading">Signals Loaded</div>
          <div className="cyrano-signals-list">
            {signals.map((s, i) => <SignalPill key={i} signal={s} />)}
          </div>
        </div>
      )}

      {account && signals.length === 0 && (
        <div className="cyrano-sidebar-section">
          <div className="cyrano-no-signals">
            <span>⚠️</span>
            <p>No signals for this account. Outreach will be less targeted without fresh triggers.</p>
          </div>
        </div>
      )}

      {contact && (
        <div className="cyrano-sidebar-section">
          <div className="cyrano-sidebar-heading">Writing To</div>
          <div className="cyrano-contact-card">
            <div className="cyrano-contact-name">{contact.name}</div>
            {contact.title && <div className="cyrano-contact-title">{contact.title}</div>}
            {contact.email && <div className="cyrano-contact-email">{contact.email}</div>}
            {contact.influenceLevel && (
              <div className="cyrano-contact-influence">
                <span>Influence:</span> {contact.influenceLevel}
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STAGING TAB — Review, approve, edit, and send pending drafts
// ─────────────────────────────────────────────────────────────────────────────

function StagingTab({ initialDraftId }) {
  const [drafts,        setDrafts]       = useState([]);
  const [loading,       setLoading]      = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [editBody,      setEditBody]     = useState('');
  const [editSubject,   setEditSubject]  = useState('');
  const [acting,        setActing]       = useState(null); // draftId being actioned
  const [toast,         setToast]        = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const loadDrafts = async () => {
    setLoading(true);
    try {
      const res  = await apiFetch('/api/cyrano/drafts/list?status=Pending Approval');
      const data = await res.json();
      const list = data.drafts || [];
      setDrafts(list);
      // Auto-select initialDraftId or first draft
      if (initialDraftId) {
        const target = list.find(d => d.id === initialDraftId) || list[0] || null;
        if (target) { setSelectedDraft(target); setEditBody(target.body || ''); setEditSubject(target.subject || ''); }
      } else if (!selectedDraft && list.length > 0) {
        setSelectedDraft(list[0]);
        setEditBody(list[0].body || '');
        setEditSubject(list[0].subject || '');
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadDrafts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectDraft = (draft) => {
    setSelectedDraft(draft);
    setEditBody(draft.body || '');
    setEditSubject(draft.subject || '');
  };

  const updateStatus = async (draftId, status) => {
    setActing(draftId);
    try {
      const body = { draftId, status };
      if (status === 'Approved' && selectedDraft?.id === draftId) {
        body.body    = editBody;
        body.subject = editSubject;
      }
      await apiFetch('/api/cyrano/drafts/update', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      showToast(status === 'Approved' ? '✅ Approved' : status === 'Rejected' ? '✕ Rejected' : '✅ Sent');
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      setSelectedDraft(null);
    } catch { showToast('Action failed — try again'); }
    finally { setActing(null); }
  };

  const handleSend = async (draft) => {
    setActing(draft.id);
    try {
      // Send through Graph as the signed-in user (P0.23 A3)
      const res = await apiFetch('/api/comms/send', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: draft.to_email, subject: editSubject, body: editBody }),
      });
      const data = await res.json();
      if (data.success) {
        await updateStatus(draft.id, 'Sent');
        showToast('✅ Sent to ' + (draft.to_email || draft.to_name));
      } else {
        // Fallback: copy + approve
        navigator.clipboard?.writeText(editBody).catch(() => {});
        await updateStatus(draft.id, 'Approved');
        showToast('📋 Copied to clipboard — paste into Outlook');
      }
    } catch {
      navigator.clipboard?.writeText(editBody).catch(() => {});
      await updateStatus(draft.id, 'Approved');
      showToast('📋 Copied to clipboard — paste into Outlook');
    } finally { setActing(null); }
  };

  if (loading) return <div className="cyrano-staging-empty">Loading drafts...</div>;

  if (drafts.length === 0) return (
    <div className="cyrano-staging-empty">
      <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
      <div style={{ fontWeight: 700, color: '#111827', marginBottom: 6 }}>Staging is clear</div>
      <div style={{ color: 'var(--dm-grey-600)', fontSize: 13 }}>Drafts staged from The Floor will appear here for review.</div>
    </div>
  );

  return (
    <div className="cyrano-staging-root">
      {toast && <div className="cyrano-staging-toast">{toast}</div>}

      {/* Draft list */}
      <div className="cyrano-staging-list">
        <div className="cyrano-staging-list-header">
          Pending Review <span className="cyrano-staging-count">{drafts.length}</span>
        </div>
        {drafts.map(draft => (
          <div key={draft.id}
            className={'cyrano-staging-item' + (selectedDraft?.id === draft.id ? ' selected' : '')}
            onClick={() => selectDraft(draft)}>
            <div className="cyrano-staging-item-name">{draft.to_name || 'Unknown'}</div>
            <div className="cyrano-staging-item-account">{draft.account_name || ''}</div>
            <div className="cyrano-staging-item-subject">{(draft.subject || '').slice(0, 55)}</div>
            <div className="cyrano-staging-item-type">{draft.draft_type || 'Outreach'}</div>
          </div>
        ))}
      </div>

      {/* Draft editor */}
      {selectedDraft && (
        <div className="cyrano-staging-editor">
          <div className="cyrano-staging-editor-header">
            <div>
              <div className="cyrano-staging-editor-to">
                To: <strong>{selectedDraft.to_name || 'Unknown'}</strong>
                {selectedDraft.to_email && <span style={{ color: 'var(--dm-grey-600)' }}> &lt;{selectedDraft.to_email}&gt;</span>}
                {selectedDraft.account_name && <span style={{ color: 'var(--dm-purple-vivid)' }}> — {selectedDraft.account_name}</span>}
              </div>
              <div className="cyrano-staging-editor-type">{selectedDraft.draft_type || 'Outreach'} · Staged by Cyrano</div>
            </div>
            <div className="cyrano-staging-editor-actions">
              <button className="cyrano-staging-btn cyrano-staging-btn--reject"
                disabled={acting === selectedDraft.id}
                onClick={() => updateStatus(selectedDraft.id, 'Rejected')}>
                ✕ Reject
              </button>
              <button className="cyrano-staging-btn cyrano-staging-btn--approve"
                disabled={acting === selectedDraft.id}
                onClick={() => updateStatus(selectedDraft.id, 'Approved')}>
                ✓ Approve
              </button>
              <button className="cyrano-staging-btn cyrano-staging-btn--send"
                disabled={acting === selectedDraft.id}
                onClick={() => handleSend(selectedDraft)}>
                {acting === selectedDraft.id ? '⏳' : '↗ Send'}
              </button>
            </div>
          </div>

          <div className="cyrano-staging-subject-row">
            <label>Subject</label>
            <input className="cyrano-staging-subject-input"
              value={editSubject}
              onChange={e => setEditSubject(e.target.value)} />
          </div>

          <textarea className="cyrano-staging-body"
            value={editBody}
            onChange={e => setEditBody(e.target.value)}
            rows={18} />

          <div className="cyrano-staging-footer">
            {selectedDraft.signal_used && (
              <div className="cyrano-staging-signal">
                📡 Signal: {selectedDraft.signal_used}
              </div>
            )}
            <div className="cyrano-staging-footer-hint">Edit directly above, then Approve or Send.</div>
          </div>
        </div>
      )}
    </div>
  );
}



export default function CyranoPage({ onNavigate, initialTab, initialContext }) {
  const { commsName } = useProfile();
  const [activeTab,    setActiveTab]    = useState(initialTab || 'compose');
  const [account,      setAccount]      = useState(null);
  const [contact,      setContact]      = useState(null);
  const [signals,      setSignals]      = useState([]);
  const [outreachType, setOutreachType] = useState('first_touch');
  const [customBrief,  setCustomBrief]  = useState('');
  const [stagingBadge, setStagingBadge] = useState(0);

  // Load staging badge count
  useEffect(() => {
    apiFetch('/api/cyrano/drafts/list?status=Pending Approval')
      .then(r => r.json())
      .then(d => setStagingBadge((d.drafts || []).length))
      .catch(() => {});
  }, []);

  // If opened from Today page with a specific draft, go to staging
  const initialDraftId = initialContext?.draftId || null;
  useEffect(() => {
    if (initialDraftId) setActiveTab('staging');
  }, [initialDraftId]);

  useEffect(() => {
    if (!account?.id) { setSignals([]); return; }
    apiFetch(`/api/signals/by-account?accountId=${account.id}`)
      .then(r => r.json())
      .then(d => { if (d.signals) setSignals(d.signals); })
      .catch(() => setSignals([]));
  }, [account?.id]);

  const handleAccountChange = (acct) => {
    setAccount(acct);
    setContact(null);
    setSignals([]);
  };

  const handleOutreachTypeChange = (type) => {
    setOutreachType(type);
    // Clear custom brief when switching away from custom
    if (type !== 'custom') setCustomBrief('');
  };

  const showSidebar = activeTab !== 'library' && activeTab !== 'content' && activeTab !== 'staging';
  const showChat    = activeTab !== 'library' && activeTab !== 'staging';
  const isContent   = activeTab === 'content';

  return (
    <div className="cyrano-root">
      <div className="cyrano-page-header">
        <div className="cyrano-page-header-left">
          {/* ✍️ — the same glyph as this page's AI-strip button. */}
          <span className="dm-page-mark dm-page-mark--cyrano">✍️</span>
          <div>
            <div className="cyrano-page-title">{commsName || 'Cyrano'} Communications Studio</div>
            <div className="cyrano-page-sub">Signal-grounded outreach, written from live account intelligence</div>
          </div>
        </div>
        <div className="cyrano-online-badge">
          <div className="cyrano-online-dot" />
          Online
        </div>
      </div>

      <div className="cyrano-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`cyrano-tab ${activeTab === tab.id ? 'cyrano-tab--active' : ''}`}
            onClick={(e) => {
              setActiveTab(tab.id);
              // The row scrolls horizontally on narrow viewports, so the tab
              // just selected can sit half-cut at the edge with no way to see
              // which is active. Centre it. On the click handler rather than a
              // ref callback: a ref fires on every render and would fight the
              // user's own scrolling. Optional-chained because older Safari
              // ignores scrollIntoView options.
              e.currentTarget.scrollIntoView?.({ block: 'nearest', inline: 'center' });
            }}
            type="button"
          >
            <span className="cyrano-tab-icon">{tab.icon}</span>
            {tab.label}
            {tab.id === 'staging' && stagingBadge > 0 && (
              <span className="cyrano-staging-badge">{stagingBadge}</span>
            )}
          </button>
        ))}
      </div>

      <div className="cyrano-layout">
        {showSidebar && (
          <ContextSidebar
            tab={activeTab}
            account={account}
            contact={contact}
            signals={signals}
            outreachType={outreachType}
            customBrief={customBrief}
            onChangeAccount={handleAccountChange}
            onChangeContact={setContact}
            onChangeOutreachType={handleOutreachTypeChange}
            onChangeCustomBrief={setCustomBrief}
          />
        )}

        {showChat && !isContent && (
          <ChatPanel
            tab={activeTab}
            account={account}
            contact={contact}
            signals={signals}
            outreachType={outreachType}
            customBrief={customBrief}
            initialContext={initialContext}
          />
        )}

        {isContent && (
          <ContentChatPanel signals={signals} />
        )}

        {activeTab === 'staging' && (
          <div className="cyrano-staging-wrap">
            <StagingTab initialDraftId={initialDraftId} />
          </div>
        )}

        {activeTab === 'library' && (
          <div className="cyrano-library-wrap">
            <LibraryTab />
          </div>
        )}
      </div>
    </div>
  );
}
